'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAccordion } from '@/hooks/useAccordion';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { trackFunnelStep } from '@/lib/analytics';
import { AccordionSection } from './AccordionSection';
import { useOrderStore } from '@/store/useOrderStore';
import { cn } from '@/lib/utils';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { QuoteRequest } from '@/types';

import Step1ItemInfo from '@/components/steps/Step1ItemInfo';
import Step1QuotedItemReadonly from '@/components/steps/Step1QuotedItemReadonly';
import Step1QuoteInquiry from '@/components/steps/Step1QuoteInquiry';
import Step2Address from '@/components/steps/Step2Address';
import Step3PriceEstimate from '@/components/steps/Step3PriceEstimate';
import Step4Schedule from '@/components/steps/Step4Schedule';
import Step5Payment from '@/components/steps/Step5Payment';

const getDirectSections = (store: ReturnType<typeof useOrderStore.getState>) => [
  {
    icon: '📦',
    title: '물품 정보',
    getStatus: () => {
      if (store.itemInfo.isCustom) return '커스텀 물품';
      if (store.items.length >= 2) {
        return `${store.items[0].displayName} 외 ${store.items.length - 1}건`;
      }
      if (store.items.length === 1) return store.items[0].displayName;
      if (store.itemInfo.itemId) return store.itemInfo.category || '선택 완료';
      return '미선택';
    },
  },
  {
    icon: '📍',
    title: '출발지 / 도착지',
    getStatus: () => {
      if (store.origin && store.destination) {
        const originDistrict = store.origin.address.split(' ')[1] || '';
        const destDistrict = store.destination.address.split(' ')[1] || '';
        return `${originDistrict} → ${destDistrict}`;
      }
      return '미입력';
    },
  },
  {
    icon: '💰',
    title: '기본 운송비',
    getStatus: () =>
      store.priceBreakdown
        ? `${store.priceBreakdown.totalPrice.toLocaleString()}원`
        : '미확인',
  },
  {
    icon: '📅',
    title: '배송 일정',
    getStatus: () => {
      if (store.scheduleDate) {
        const parts = [store.scheduleDate];
        if (store.designatedPickupSlot) parts.push(`수거 ${store.designatedPickupSlot}`);
        if (store.designatedDeliverySlot) parts.push(`배송 ${store.designatedDeliverySlot}`);
        return parts.join(' ');
      }
      return '미선택';
    },
  },
  {
    icon: '✅',
    title: '결제 및 완료',
    getStatus: () => '대기',
  },
];

const getQuoteInquirySections = (store: ReturnType<typeof useOrderStore.getState>) => [
  {
    icon: '📦',
    title: '물품 정보',
    getStatus: () => {
      if (store.itemInfo.isCustom && store.itemInfo.customData) {
        const has = store.itemInfo.customData.imageUrls.length > 0 || store.itemInfo.customData.linkUrl;
        return has ? '입력 완료' : '미입력';
      }
      return '미입력';
    },
  },
  {
    icon: '📍',
    title: '출발지 / 도착지',
    getStatus: () => {
      if (store.origin && store.destination) {
        const originDistrict = store.origin.address.split(' ')[1] || '';
        const destDistrict = store.destination.address.split(' ')[1] || '';
        return `${originDistrict} → ${destDistrict}`;
      }
      return '미입력';
    },
  },
  {
    icon: '📅',
    title: '배송 일정',
    getStatus: () => {
      if (store.scheduleDate) {
        const parts = [store.scheduleDate];
        if (store.designatedPickupSlot) parts.push(`수거 ${store.designatedPickupSlot}`);
        if (store.designatedDeliverySlot) parts.push(`배송 ${store.designatedDeliverySlot}`);
        return parts.join(' ');
      }
      return '미선택';
    },
  },
];

interface Props {
  quoteId?: string | null;
  resumeFrom?: string | null;
}

function DirectAccordionContent({ quoteData, onSwitchToQuote }: { quoteData: QuoteRequest | null; onSwitchToQuote?: () => void }) {
  const store = useOrderStore();
  const sections = getDirectSections(store);
  const isQuoteMode = !!quoteData;
  const hasFullQuoteData = isQuoteMode && !!quoteData.addressData?.origin && !!quoteData.addressData?.destination && !!quoteData.scheduleData?.date;

  const { currentSection, completedSections, openSection, completeSection, canOpen } =
    useAccordion({
      totalSections: 5,
      initialSection: hasFullQuoteData ? 4 : isQuoteMode ? 1 : 0,
      initialCompletedSections: hasFullQuoteData
        ? [true, true, true, true, false]
        : isQuoteMode
          ? [true, false, false, false, false]
          : undefined,
    });
  const { setRef, scrollToSection } = useScrollToSection();
  const prevSectionRef = useRef(currentSection);

  useEffect(() => {
    useOrderStore.getState().setCurrentStep(currentSection + 1, 5);
  }, [currentSection]);

  useEffect(() => {
    if (prevSectionRef.current === currentSection) return;
    prevSectionRef.current = currentSection;
    scrollToSection(currentSection);
  }, [currentSection, scrollToSection]);

  const stepNames = ['item_info', 'address', 'price_estimate', 'schedule', 'payment_info'];

  const handleComplete = (index: number) => {
    completeSection(index);
    trackFunnelStep(index + 1, stepNames[index]);
  };

  return (
    <div className="space-y-2">
      {sections.map((section, index) => (
        <AccordionSection
          key={index}
          ref={setRef(index)}
          icon={section.icon}
          title={section.title}
          statusText={section.getStatus()}
          isOpen={currentSection === index}
          isComplete={completedSections[index]}
          isDisabled={!canOpen(index)}
          onToggle={() => openSection(index)}
        >
          {index === 0 &&
            (isQuoteMode ? (
              <Step1QuotedItemReadonly
                itemName={quoteData.approvedItemName!}
                price={quoteData.approvedPrice!}
              />
            ) : (
              <Step1ItemInfo onComplete={() => handleComplete(0)} onSwitchToQuote={onSwitchToQuote} />
            ))}
          {index === 1 && <Step2Address onComplete={() => handleComplete(1)} />}
          {index === 2 && <Step3PriceEstimate onComplete={() => handleComplete(2)} />}
          {index === 3 && <Step4Schedule onComplete={() => handleComplete(3)} />}
          {index === 4 && <Step5Payment />}
        </AccordionSection>
      ))}
    </div>
  );
}

function QuoteInquiryAccordionContent({ onSubmitted }: { onSubmitted: () => void }) {
  const store = useOrderStore();
  const sections = getQuoteInquirySections(store);
  const [submitting, setSubmitting] = useState(false);

  const { currentSection, completedSections, openSection, completeSection, canOpen } =
    useAccordion({ totalSections: 3 });
  const { setRef, scrollToSection } = useScrollToSection();
  const prevSectionRef = useRef(currentSection);

  useEffect(() => {
    useOrderStore.getState().setCurrentStep(currentSection + 1, 3);
  }, [currentSection]);

  useEffect(() => {
    if (prevSectionRef.current === currentSection) return;
    prevSectionRef.current = currentSection;
    scrollToSection(currentSection);
  }, [currentSection, scrollToSection]);

  const stepNames = ['quote_item_info', 'quote_address', 'quote_schedule'];

  const handleComplete = (index: number) => {
    completeSection(index);
    trackFunnelStep(index + 1, stepNames[index]);
  };

  const handleQuoteSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customData: store.itemInfo.customData,
          phone: store.phone,
          addressData: store.origin && store.destination ? {
            origin: store.origin,
            destination: store.destination,
          } : undefined,
          scheduleData: store.scheduleDate ? {
            date: store.scheduleDate,
            pickupSlot: store.designatedPickupSlot,
            deliverySlot: store.designatedDeliverySlot,
            flexPickupMorning: store.flexiblePickupMorning,
            flexPickupAfternoon: store.flexiblePickupAfternoon,
            flexDeliveryMorning: store.flexibleDeliveryMorning,
            flexDeliveryAfternoon: store.flexibleDeliveryAfternoon,
            flexDeliveryAnytime: store.flexibleDeliveryAnytime,
            showDeliveryTimePanel: store.showDeliveryTimePanel,
            pickupDiscountRate: store.pickupDiscountRate,
            deliveryDiscountRate: store.deliveryDiscountRate,
          } : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      onSubmitted();
    } catch (err) {
      console.error('Quote request failed:', err);
      alert('견적 요청에 실패했습니다. 다시 시도해주세요.');
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-2">
      {sections.map((section, index) => (
        <AccordionSection
          key={index}
          ref={setRef(index)}
          icon={section.icon}
          title={section.title}
          statusText={section.getStatus()}
          isOpen={currentSection === index}
          isComplete={completedSections[index]}
          isDisabled={!canOpen(index)}
          onToggle={() => openSection(index)}
        >
          {index === 0 && <Step1QuoteInquiry onComplete={() => handleComplete(0)} />}
          {index === 1 && <Step2Address onComplete={() => handleComplete(1)} />}
          {index === 2 && (
            <Step4Schedule
              onComplete={handleQuoteSubmit}
              submitLabel="견적 요청"
              submitLoading={submitting}
            />
          )}
        </AccordionSection>
      ))}
    </div>
  );
}

export default function AccordionContainer({ quoteId, resumeFrom }: Props) {
  const store = useOrderStore();
  const router = useRouter();
  const [quoteData, setQuoteData] = useState<QuoteRequest | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(!!quoteId || !!resumeFrom);
  const [mode, setMode] = useState<'direct' | 'quote'>(quoteId && !resumeFrom ? 'direct' : 'quote');
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [showQuoteGuide, setShowQuoteGuide] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (resumeFrom) {
      fetch(`/api/quotes?quoteId=${resumeFrom}`)
        .then((res) => res.json())
        .then((data: QuoteRequest) => {
          const cd = data.customData as { imageUrls?: string[]; linkUrl?: string; description?: string } | null;
          store.setItemInfo({
            isCustom: true,
            itemId: null,
            category: null,
            basePrice: 0,
            options: {},
            customData: {
              imageUrls: cd?.imageUrls ?? [],
              linkUrl: cd?.linkUrl ?? '',
              description: cd?.description ?? '',
            },
          });
          store.setPhone(data.phone);
          if (data.addressData?.origin) {
            const o = data.addressData.origin;
            store.setOrigin({
              address: o.address,
              addressDetail: (o as unknown as Record<string, unknown>).addressDetail as string || o.detail || '',
              lat: o.lat,
              lng: o.lng,
              hasElevator: o.hasElevator ?? true,
              floor: o.floor ?? 1,
            });
          }
          if (data.addressData?.destination) {
            const d = data.addressData.destination;
            store.setDestination({
              address: d.address,
              addressDetail: (d as unknown as Record<string, unknown>).addressDetail as string || d.detail || '',
              lat: d.lat,
              lng: d.lng,
              hasElevator: d.hasElevator ?? true,
              floor: d.floor ?? 1,
            });
          }
        })
        .catch((err) => {
          console.error('Failed to resume from quote:', err);
        })
        .finally(() => {
          setQuoteLoading(false);
        });
      return;
    }

    if (!quoteId) return;

    fetch(`/api/quotes?quoteId=${quoteId}`)
      .then((res) => res.json())
      .then((data: QuoteRequest) => {
        if (data.isExpired) {
          setIsExpired(true);
          return;
        }
        if (data.status === 'APPROVED' && data.approvedItemName && data.approvedPrice !== null) {
          setQuoteData(data);
          const cd = data.customData as { imageUrls?: string[]; linkUrl?: string; description?: string } | null;
          store.setItemInfo({
            isCustom: false,
            itemId: 'QUOTED',
            category: data.approvedItemName,
            basePrice: data.approvedPrice,
            options: {},
            customData: cd?.imageUrls?.length ? { imageUrls: cd.imageUrls, linkUrl: cd.linkUrl || '', description: cd.description || '' } : undefined,
          });
          store.setPhone(data.phone);
          store.setQuoteId(quoteId);

          // 주소 데이터 프리필
          if (data.addressData?.origin) {
            const o = data.addressData.origin;
            store.setOrigin({
              address: o.address,
              addressDetail: (o as unknown as Record<string, unknown>).addressDetail as string || o.detail || '',
              lat: o.lat,
              lng: o.lng,
              hasElevator: o.hasElevator ?? true,
              floor: o.floor ?? 1,
            });
          }
          if (data.addressData?.destination) {
            const d = data.addressData.destination;
            store.setDestination({
              address: d.address,
              addressDetail: (d as unknown as Record<string, unknown>).addressDetail as string || d.detail || '',
              lat: d.lat,
              lng: d.lng,
              hasElevator: d.hasElevator ?? true,
              floor: d.floor ?? 1,
            });
          }

          // 일정 데이터 프리필
          if (data.scheduleData?.date) {
            store.setSchedule({
              date: data.scheduleData.date,
              pickupSlot: data.scheduleData.pickupSlot,
              deliverySlot: data.scheduleData.deliverySlot,
              flexPickupMorning: data.scheduleData.flexPickupMorning,
              flexPickupAfternoon: data.scheduleData.flexPickupAfternoon,
              flexDeliveryMorning: data.scheduleData.flexDeliveryMorning,
              flexDeliveryAfternoon: data.scheduleData.flexDeliveryAfternoon,
              flexDeliveryAnytime: data.scheduleData.flexDeliveryAnytime,
              showDeliveryPanel: data.scheduleData.showDeliveryTimePanel,
            });
          }

          // 주소+일정 모두 있으면 안내 팝업 표시
          if (data.addressData?.origin && data.addressData?.destination && data.scheduleData?.date) {
            setShowQuoteGuide(true);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to fetch quote:', err);
      })
      .finally(() => {
        setQuoteLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, resumeFrom]);

  const handleModeChange = (newMode: 'direct' | 'quote') => {
    if (newMode === mode) return;
    store.reset();
    setMode(newMode);
    setQuoteSubmitted(false);
  };

  if (quoteLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">견적 정보를 불러오는 중...</p>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border p-8 text-center space-y-6">
        <h2 className="text-xl font-bold text-gray-900">요청하신 견적이 초기화되었습니다</h2>
        <p className="text-sm text-gray-600">
          예약을 원하시면 다시 견적을 요청해 주시기 바랍니다.
        </p>
        <Button
          onClick={() => router.push(`/reservation?resumeFrom=${quoteId}`)}
          className="w-full bg-[#1A3A5C] hover:bg-[#15304D] text-white"
        >
          다시 견적 요청하기
        </Button>
      </div>
    );
  }

  // 견적 제출 성공
  if (quoteSubmitted) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm border p-8 text-center space-y-6">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
        <h2 className="text-xl font-bold text-gray-900">견적이 요청되었습니다</h2>
        <p className="text-sm text-gray-600">
          입력하신 <strong>{store.phone}</strong> 번호로<br />
          최대한 빠르게 답변 드리도록 하겠습니다.
        </p>
        <Button onClick={() => router.push('/')} variant="outline" className="w-full">
          홈으로 돌아가기
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 견적 링크 안내 팝업 */}
      <Dialog open={showQuoteGuide} onOpenChange={setShowQuoteGuide}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">안내</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 text-center leading-relaxed">
            출발지와 도착지, 일정 등 중요 정보를<br />
            다시 한번 확인 후 결제 진행해주세요.
          </p>
          <Button onClick={() => setShowQuoteGuide(false)} className="w-full mt-2">
            확인
          </Button>
        </DialogContent>
      </Dialog>

      {/* 모드 선택 (approved-quote 모드가 아닐 때만 표시) */}
      {!quoteData && (
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => handleModeChange('direct')}
            className={cn(
              'py-3 px-4 rounded-lg text-sm font-medium transition-all border-2',
              mode === 'direct'
                ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
            )}
          >
            직접 선택
          </button>
          <button
            onClick={() => handleModeChange('quote')}
            className={cn(
              'py-3 px-4 rounded-lg text-sm font-medium transition-all border-2',
              mode === 'quote'
                ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
            )}
          >
            견적 문의
          </button>
        </div>
      )}

      {/* 아코디언 콘텐츠 */}
      {mode === 'direct' ? (
        <DirectAccordionContent key="direct" quoteData={quoteData} onSwitchToQuote={() => handleModeChange('quote')} />
      ) : (
        <QuoteInquiryAccordionContent key="quote" onSubmitted={() => setQuoteSubmitted(true)} />
      )}
    </div>
  );
}
